// Notifications bell + dropdown for the App Shell header
// (T-035, plan §5.4, spec §5.4).
//
// Bell icon with an unread-count badge. Click opens a popover that
// lists the most recent 20 notifications. Each row is a clickable
// link that marks itself read on click; a "Mark all read" button at
// the top of the popover marks every unread notification.
//
// Polls /api/me/notifications/unread-count every 60 s while the page
// is open. The full list is fetched lazily (only when the popover
// opens) to keep idle bandwidth low.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '../api/client';
import type {
  NotificationItem,
  NotificationListResponse,
  UnreadNotificationCount,
} from '@pa/shared';

const POLL_INTERVAL_MS = 60_000;

export function NotificationsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Lightweight count poll — runs even when the popover is closed
  // so the badge stays current. The fuller list is only fetched
  // when the popover opens.
  const countQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () =>
      api.get<UnreadNotificationCount>('/api/me/notifications/unread-count'),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  // Full list — fetched only when the popover is open. `enabled` keeps
  // closed-state idle bandwidth at zero beyond the cheap count poll.
  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () =>
      api.get<NotificationListResponse>('/api/me/notifications'),
    enabled: open,
    staleTime: 5_000,
  });

  // Click outside / Escape close.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markOne = useMutation({
    mutationFn: (id: string) =>
      api.patch<NotificationItem>(`/api/me/notifications/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; marked: number }>(
        '/api/me/notifications/read-all',
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  function pickNotification(n: NotificationItem) {
    setOpen(false);
    if (!n.readAt) markOne.mutate(n.id);
    if (n.href) navigate(n.href);
  }

  const count = countQuery.data?.count ?? 0;
  const items = listQuery.data?.items ?? [];

  return (
    <div ref={containerRef} className="relative" data-testid="notifications-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          count > 0 ? `Notifications (${count} unread)` : 'Notifications'
        }
        className="relative w-10 h-10 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
        data-testid="notifications-bell-trigger"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            data-testid="notifications-bell-badge"
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-slate-100 z-50"
          data-testid="notifications-bell-menu"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Notifications
            </h3>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 disabled:opacity-50"
                data-testid="notifications-bell-markall"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
          <div
            className="max-h-96 overflow-y-auto"
            data-testid="notifications-bell-list"
          >
            {listQuery.isLoading && (
              <p className="px-4 py-6 text-sm text-slate-400">Loading…</p>
            )}
            {listQuery.isError && (
              <p
                className="px-4 py-6 text-sm text-rose-600"
                data-testid="notifications-bell-error"
              >
                Couldn't load notifications.
              </p>
            )}
            {listQuery.data && items.length === 0 && (
              <p className="px-4 py-6 text-sm text-slate-400">
                No notifications yet.
              </p>
            )}
            <ul>
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`border-b border-slate-50 last:border-b-0 ${
                    n.readAt ? '' : 'bg-slate-50/50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => pickNotification(n)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50"
                    data-testid={`notifications-bell-item-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {!n.readAt && (
                        <span
                          aria-hidden="true"
                          className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-700 truncate">
                          {n.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {n.body}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {formatRelative(n.createdAt)}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = 60 * 1000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (diff < min) return 'just now';
  if (diff < hour) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}
